import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

import { IMetricsOverview, ITimeseriesPoint, defaultValue } from 'app/shared/model/admin-metrics.model';
import { serializeAxiosError } from 'app/shared/reducers/reducer.utils';

const initialState = {
  loading: false,
  seriesLoading: false,
  errorMessage: null as string | null,
  overview: defaultValue as IMetricsOverview,
  series: [] as readonly ITimeseriesPoint[],
};

export type AdminMetricsState = Readonly<typeof initialState>;

const overviewUrl = 'api/admin/metrics/overview';
const timeseriesUrl = 'api/admin/metrics/timeseries';

const dateRangeQuery = ({ from, to }: { from?: string | null; to?: string | null }) => {
  const qp: string[] = [];
  if (from) qp.push(`from=${from}`);
  if (to) qp.push(`to=${to}`);
  return qp.length ? `?${qp.join('&')}` : '';
};

export const getOverview = createAsyncThunk(
  'adminMetrics/fetch_overview',
  async (range: { from?: string | null; to?: string | null }, { signal }) => {
    return axios.get<IMetricsOverview>(`${overviewUrl}${dateRangeQuery(range)}`, { signal });
  },
  { serializeError: serializeAxiosError },
);

export const getTimeseries = createAsyncThunk(
  'adminMetrics/fetch_timeseries',
  async (range: { from?: string | null; to?: string | null }, { signal }) => {
    return axios.get<{ points: ITimeseriesPoint[] }>(`${timeseriesUrl}${dateRangeQuery(range)}`, { signal });
  },
  { serializeError: serializeAxiosError },
);

export const AdminMetricsSlice = createSlice({
  name: 'adminMetrics',
  initialState: initialState as AdminMetricsState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getOverview.pending, state => {
        state.loading = true;
        state.errorMessage = null;
      })
      .addCase(getOverview.fulfilled, (state, action) => {
        state.loading = false;
        state.overview = action.payload.data;
      })
      .addCase(getOverview.rejected, (state, action) => {
        if (action.meta?.aborted) return; // superseded by a newer request — ignore
        state.loading = false;
        state.errorMessage = action.error.message!;
      })
      .addCase(getTimeseries.pending, state => {
        state.seriesLoading = true;
      })
      .addCase(getTimeseries.fulfilled, (state, action) => {
        state.seriesLoading = false;
        state.series = action.payload.data.points ?? [];
      })
      .addCase(getTimeseries.rejected, (state, action) => {
        if (action.meta?.aborted) return; // superseded by a newer request — ignore
        state.seriesLoading = false;
        state.errorMessage = action.error.message!;
      });
  },
});

export const { reset } = AdminMetricsSlice.actions;

export default AdminMetricsSlice.reducer;
